// @flow
import * as React from 'react';
import { Formik } from 'formik';
import { isEmpty, snakeCase } from 'lodash';
import { FormattedMessage } from 'react-intl';
import { Box } from 'grommet';
import SelectMenuPlacementContext from 'esm-web-pro/contexts/SelectMenuPlacementContext';
import ApiForm from 'speed-js-react-web/src/containers/grommet2/forms/ApiForm';
import ActionButtons from 'esm-web-pro/components/forms/ActionButtons';
import { APIContext } from 'speed-js-react';
import SidePane from '../../layout/SidePane';

type Props = {
    initialData: Object,
    allow_new: boolean,
    addingNew: boolean,
    addressRef: React.ElementRef<Formik>,
    addNewFullForm: boolean,
    newAddressDescription: string,
    oldAddressTitle: string,
    value: any,
    id: string,
    menuContextValue: Object,
    formikRef: React.ElementRef<Formik>,
    handleNewAddressSubmit: Function,
    handleNewAddressClose: Function,
    renderNewAddressTownPicker: Function,
    renderRegion: Function,
    renderNewAddressTextInput: Function,
    townId: string,
    qName: string,
    ekatte: string,
};

const FULL_EXTRA_SCHEMA = [{
    'town_id.type': 'choice',
    'town_id.ui.resource_name': 'city',
    'town_id.ui.label_fields': ['name'],
    'town_id.ui.value_field': ['name'],
    'town_id.validation.required': true,
}];

const RenderNewAddressSidePanel = (props: Props) => {
    const {
        initialData, allow_new: allowNew, addingNew, addressRef, addNewFullForm, newAddressDescription,
        oldAddressTitle, value, id, menuContextValue, formikRef, handleNewAddressSubmit,
        handleNewAddressClose, renderNewAddressTownPicker, renderRegion, renderNewAddressTextInput,
        townId, qName, ekatte,
    } = props;

    const { apiClient } = React.useContext(APIContext);

    const [isDisabled, setIsDisabled] = React.useState(true);
    const [initial, setInitial] = React.useState(null);


    React.useEffect(() => {
        if (!addNewFullForm || !allowNew || !addingNew) return;

        let _initial = {};
        const excludedKeys = ['addingNew', 'hasEntrances', 'hasFloors'];

        Object.keys(initialData).forEach((k) => {
            if (excludedKeys.indexOf(k) === -1) {
                _initial[snakeCase(k)] = initialData[k];
            }
        });
        _initial.source = 'esm-web-pro';
        _initial.status = addNewFullForm ? 'active' : 'pending';

        const processesAdditionalData = (additionalData) => {
            delete additionalData.str_subn;
            delete additionalData.str_n;
            delete additionalData.block_n;
            delete additionalData.app_n;
            delete additionalData.entr_n;
            delete additionalData.floor_n;
            delete additionalData.geom;
            additionalData.end_address = true;

            _initial = {
                ..._initial, ...additionalData,
            };
            setInitial(_initial);
        };

        const processAddresses = (addresses, excludeQuarter) => {
            const additionalData = { ...addresses.results[0] };
            processesAdditionalData(additionalData);
            delete additionalData.str_type;
            delete additionalData.str_name;
            if (excludeQuarter) {
                delete additionalData.post_code;
                delete additionalData.q_type;
                delete additionalData.q_name;
            }
            _initial = {
                ..._initial, ...additionalData,
            };
            if (ekatte && !_initial.ekatte) {
                _initial.ekatte = ekatte;
            }
            setInitial(_initial);
        };


        if (addressRef.current && addressRef.current.state.options.length > 0) {
            let firstHorizontalOption = null;

            // We have a value if the user has selected an option from the dropdown of addresses or if it was
            // preselected by the parent component. If we have a value we check if it is from the bitmap-bulgaria
            // and if it is NOT we query the API for an address with the same name and town_id
            // and source bitmap-bulgaria
            if (value && value.source !== 'bitmap-bulgaria') {
                const query = { town_id: townId, limit: 1, source: 'bitmap-bulgaria' };
                if (value.str_name) {
                    query.str_name = value.str_name;
                }

                apiClient.resources.address.list(query).then((addresses) => {
                    if (addresses.results.length > 0) {
                        // Means we found and address with the same name and town_id and source bitmap-bulgaria
                        processAddresses(addresses, false);
                    } else {
                        // We didn't find one so we default to the preset address
                        processesAdditionalData({ ...addressRef.current.state.options[0] });
                    }
                });
            // If we don't have a value this means we recieve all the options for address from the parent component
            // and we can filter them to find the first one with source bitmap-bulgaria, if we don't find one
            // we use the first option
            } else {
                // Attempt to find the an option with source bitmap-bulgaria
                const firstHorizontalBitmapOption = addressRef.current.state.options.find((addr) => (
                    ['bitmap-bulgaria'].indexOf(addr._original.source) > -1
                ));

                // If we find a bitmap-bulgaria option we use it. Otherwise we use the first option
                if (firstHorizontalBitmapOption) {
                    firstHorizontalOption = firstHorizontalBitmapOption;
                } else {
                    // eslint-disable-next-line prefer-destructuring
                    firstHorizontalOption = addressRef.current.state.options[0];
                }

                processesAdditionalData({ ...firstHorizontalOption._original });
            }
        } else {
            const query = { town_id: townId, limit: 1 };
            if (qName) {
                query.q_name = qName;
            }
            apiClient.resources.address.list(query).then((addresses) => {
                if (!addresses.results.length && qName) {
                    delete query.q_name;
                    apiClient.resources.address.list(query).then((r) => {
                        processAddresses(r, true);
                    });
                } else {
                    processAddresses(addresses, !qName);
                }
            });
        }
    }, [
        addNewFullForm, addressRef, initialData, value, townId, qName, apiClient, allowNew, addingNew, ekatte,
    ]);

    const handleSubmit = React.useCallback(() => {
        const errors = formikRef?.current?.formRef.current.getFormikBag().errors || {};
        const formikFieldValues = formikRef?.current?.formRef.current.getFormikBag().values;
        if (isEmpty(errors)) {
            handleNewAddressSubmit(formikFieldValues);
        } else {
            setIsDisabled(true);
        }
    }, [formikRef, handleNewAddressSubmit]);

    const renderNewAddressActions = () => (
        <ActionButtons
            onSave={handleSubmit}
            onClose={handleNewAddressClose}
            disabled={isDisabled}
        />
    );

    const handleFieldValueChange = React.useCallback((formikValues, formikErrors) => {
        if (!isEmpty(formikErrors)) {
            setIsDisabled(true);
            return;
        }

        const requiredFields = ['reg_id', 'mun_id', 'ekatte', 't_type', 'town_id'];
        const missingRequiredFields = requiredFields.filter((field) => !formikValues[field]).length > 0;


        // If we don't have either str_name or q_name we need to have str_n or block_n
        const validStreet = formikValues.str_name && formikValues.str_n;
        // If we don't have either str_name or q_name we need to have str_n or block_n
        const validQuarter = formikValues.q_name && formikValues.block_n;

        const formIsValid = (validStreet || validQuarter) && !missingRequiredFields;

        setIsDisabled(!formIsValid);
    }, [setIsDisabled]);

    const extraSchema = React.useMemo(() => {
        const schema = [...FULL_EXTRA_SCHEMA];
        schema[0]['town_id.ui.onChange'] = (_v, _original) => {
            if (_original && _original.id) {
                formikRef.current.formRef.current.setFieldValue('ekatte', _original.ekatte);
            } else if (_v && _v === townId) {
                formikRef.current.formRef.current.setFieldValue('ekatte', ekatte);
            } else {
                formikRef.current.formRef.current.setFieldValue('ekatte', null);
            }
        };

        return schema;
    }, [formikRef, townId, ekatte]);

    React.useEffect(() => {
        if (!newAddressDescription) {
            setIsDisabled(true);
        } else {
            setIsDisabled(false);
        }
    }, [newAddressDescription]);

    if (!allowNew || !addingNew) return null;

    return (
        <SidePane
            id={id}
            openInitially
            title={oldAddressTitle || (
                <FormattedMessage
                    id="addresspicker-new-address"
                    defaultMessage="Add a new address"
                />
            )}
            actions={renderNewAddressActions()}
            zIndex={100}
        >
            {addNewFullForm ? (
                <Box height="100%" justify="between">
                    <SelectMenuPlacementContext.Provider value={menuContextValue}>
                        {initial && (
                            <ApiForm
                                resourceName="address"
                                initial={initial}
                                hasActions
                                excludeFields={['source', 'status']}
                                ref={formikRef}
                                onValuesChanged={handleFieldValueChange}
                                extraSchema={extraSchema}
                            />
                        )}
                    </SelectMenuPlacementContext.Provider>
                </Box>
            ) : (
                <>
                    <Box width="100%" pad={{ horizontal: 'medium' }}>
                        <Formik>
                            <>
                                {renderNewAddressTownPicker}
                                {renderRegion}
                                {renderNewAddressTextInput}
                            </>
                        </Formik>
                    </Box>
                </>
            )}
        </SidePane>
    );
};

export default RenderNewAddressSidePanel;
